import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { moveSmartProbe } from '../app/redux/slices/masterSubAccountSlice';
import { getScrollInfoV2 } from '../app/redux/slices/dataSlice';
import { selectIsAuthenticated } from '../app/redux/slices/authSlice';
import Navigation from '../app/components/NavigationSPA';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

interface Team {
  id: string;
  name: string;
  displayName: string;
  company: string;
  type: 'master' | 'sub';
  isActive: boolean;
  probesCount: number;
}

interface SmartProbe {
  trapId: string;
  trapName: string;
  location?: string;
  building?: string;
  macAddress?: string;
}

export default function MoveSmartProbePage() {
  const { t } = useTranslation(['teams', 'common', 'dashboard']);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { 
    isMasterAccount,
    allTeamsData
  } = useSelector((state: RootState) => state.masterSubAccount);

  const [mounted, setMounted] = useState(false);
  const [sourceTeam, setSourceTeam] = useState<Team | null>(null);
  const [targetTeam, setTargetTeam] = useState<Team | null>(null);
  const [selectedProbe, setSelectedProbe] = useState<SmartProbe | null>(null);
  const [currentTeamProbes, setCurrentTeamProbes] = useState<SmartProbe[]>([]);
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [isTargetDropdownOpen, setIsTargetDropdownOpen] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isFetchingProbes, setIsFetchingProbes] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Get sourceTeamId from URL query params if provided
  const sourceTeamId = searchParams.get('sourceTeamId');

  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Check authentication
  useEffect(() => {
    if (mounted && !isAuthenticated) {
      navigate('/login');
    }
  }, [mounted, isAuthenticated, navigate]);

  // Check if user is master account
  useEffect(() => {
    if (mounted && isAuthenticated && !isMasterAccount) {
      navigate('/dashboard');
    }
  }, [mounted, isAuthenticated, isMasterAccount, navigate]);

  // Pre-select source team if provided in URL
  useEffect(() => {
    if (mounted && sourceTeamId && allTeamsData) {
      const team = allTeamsData.find(t => t.id === sourceTeamId);
      if (team) {
        setSourceTeam(team);
      }
    }
  }, [mounted, sourceTeamId, allTeamsData]);

  // Fetch probes when source team is selected (matches mobile app behavior)
  useEffect(() => {
    if (mounted && sourceTeam) {
      logger.log('🔄 MoveSmartProbe: Source team changed, fetching probes');
      logger.log('📋 Source team FULL OBJECT:', JSON.stringify(sourceTeam, null, 2));
      logger.log('🏷️ Team name:', sourceTeam.name);
      logger.log('🏷️ Team displayName:', sourceTeam.displayName);
      logger.log('🏷️ Team company:', sourceTeam.company);
      logger.log('🏷️ Team type:', sourceTeam.type);
      
      // Clear the selected probe and current probes when changing source team
      setSelectedProbe(null);
      setCurrentTeamProbes([]); // Clear the list immediately
      setIsFetchingProbes(true);

      const fetchProbes = async () => {
        try {
          logger.log('🌐 Calling getScrollInfoV2 with teamName:', sourceTeam.name);
          
          // Use getScrollInfoV2 to fetch probes (same as mobile app's fetchLatestProbesData)
          const result = await dispatch(getScrollInfoV2({
            teamName: sourceTeam.name,
            page: 1,
            pageSize: 100, // Get all probes like mobile app
            filter: 'id',
            ascend: true
          })).unwrap();
          
          logger.log('✅ Fetch response FULL:', JSON.stringify(result, null, 2));
          logger.log('📊 trapLength:', result.trapLength);
          logger.log('📦 traps array length:', result.traps?.length);
          
          if (result?.traps && Array.isArray(result.traps)) {
            logger.log('✅ Setting currentTeamProbes with', result.traps.length, 'probes');
            logger.log('📋 First probe:', result.traps[0]);
            setCurrentTeamProbes(result.traps);
          } else {
            logger.log('⚠️ No traps array in response or traps is not an array');
            logger.log('Response structure:', Object.keys(result));
            setCurrentTeamProbes([]);
          }
        } catch (error) {
          logger.error('❌ Failed to fetch probes - FULL ERROR:', error);
          logger.error('❌ Error message:', (error as Error).message);
          logger.error('❌ Error stack:', (error as Error).stack);
          setCurrentTeamProbes([]);
        } finally {
          setIsFetchingProbes(false);
        }
      };
      
      fetchProbes();
    } else {
      logger.log('⏭️ Skipping probe fetch - mounted:', mounted, 'sourceTeam:', sourceTeam);
      // If no source team is selected, clear the probe list
      setSelectedProbe(null);
      setCurrentTeamProbes([]);
    }
  }, [mounted, sourceTeam, dispatch]);

  const handleSourceTeamSelect = (team: Team) => {
    logger.log('🔄 Changing source team to:', team);
    setSourceTeam(team);
    setIsSourceDropdownOpen(false);
    // Clear target team if it's the same as the new source team
    if (targetTeam && targetTeam.name === team.name) {
      setTargetTeam(null);
    }
  };

  const handleTargetTeamSelect = (team: Team) => {
    setTargetTeam(team);
    setIsTargetDropdownOpen(false);
  };

  const handleProbeSelect = (probe: SmartProbe) => {
    setSelectedProbe(probe);
  };

  const handleMoveClick = () => {
    if (sourceTeam && targetTeam && selectedProbe) {
      setShowConfirmModal(true);
    }
  };

  const handleMoveConfirm = async () => {
    if (!sourceTeam || !targetTeam || !selectedProbe) return;

    try {
      setIsMoving(true);
      setShowConfirmModal(false);

      const payload = {
        probeId: selectedProbe.trapId,
        fromTeamName: sourceTeam.name,
        toTeamName: targetTeam.name,
        probeType: 'trap' as 'trap' | 'rodent'
      };

      logger.log('🔄 Moving SmartProbe');
      logger.log('📦 Full Payload:', JSON.stringify(payload, null, 2));
      logger.log('🔍 Selected Probe:', JSON.stringify(selectedProbe, null, 2));
      logger.log('👥 Source Team:', JSON.stringify(sourceTeam, null, 2));
      logger.log('🎯 Target Team:', JSON.stringify(targetTeam, null, 2));

      const result = await dispatch(moveSmartProbe(payload)).unwrap();

      logger.log('✅ Move successful');
      logger.log('📋 Result:', JSON.stringify(result, null, 2));

      // Show success message
      const probeName = selectedProbe.trapName || `ID: ${selectedProbe.trapId}`;
      alert(`${probeName} ${t('moveSuccessful')}`);
      
      // Return to teams details page if we came from there, otherwise go to teams list
      if (sourceTeamId) {
        navigate(`/teams/details/${sourceTeamId}`);
      } else {
        navigate('/teams');
      }
    } catch (error: any) {
      logger.error('❌ Move error - Full error object:', error);
      logger.error('❌ Error message:', error?.message);
      logger.error('❌ Error code:', error?.code);
      alert(`${t('moveSmartProbeFailed')} ${error?.message || 'Unknown error'}`);
    } finally {
      setIsMoving(false);
    }
  };

  // Get available teams (exclude the source team from target dropdown)
  const availableTargetTeams = allTeamsData?.filter(t => 
    t.id !== sourceTeam?.id && t.isActive
  ) || [];
  
  // Debug logging for target teams
  useEffect(() => {
    if (sourceTeam) {
      logger.log('🎯 === TARGET TEAM FILTERING DEBUG ===');
      logger.log('📋 All teams data:', allTeamsData);
      logger.log('🔵 Source team ID:', sourceTeam?.id);
      logger.log('🔵 Source team name:', sourceTeam?.name);
      logger.log('📊 Available target teams:', availableTargetTeams);
      logger.log('📊 Target teams count:', availableTargetTeams.length);
      logger.log('🔍 Master account in list?', allTeamsData?.find(t => t.type === 'master'));
      logger.log('🔍 Master account filtered out?', allTeamsData?.find(t => t.type === 'master' && t.id !== sourceTeam?.id));
      logger.log('🎯 === END DEBUG ===');
    }
  }, [sourceTeam, allTeamsData, availableTargetTeams]);

  if (!mounted || !isAuthenticated || !isMasterAccount) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('moveSmartProbeTitle')}</h1>
              <p className="mt-1 text-sm text-gray-900">{t('transferSmartProbeDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Source and Target Team Selection */}
          <div className="space-y-6">
            {/* Source Team Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('sourceTeam')}</h2>
            <p className="text-sm text-gray-900 mb-4">{t('selectTeamOwnsProbe')}</p>
            
            <div className="relative">
              <button
                onClick={() => setIsSourceDropdownOpen(!isSourceDropdownOpen)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:bg-blue-50 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
              >
                {sourceTeam ? (
                  <div>
                    <p className="font-medium text-gray-900">{sourceTeam.company}</p>
                    <p className="text-sm text-gray-900">{sourceTeam.displayName}</p>
                  </div>
                ) : (
                  <span className="text-gray-800">{t('selectSourceTeam')}</span>
                )}
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${isSourceDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isSourceDropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {allTeamsData && allTeamsData.length > 0 ? (
                    allTeamsData.filter(t => t.isActive).map((team) => (
                      <button
                        key={team.id}
                        onClick={() => handleSourceTeamSelect(team)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-100 hover:border-blue-300 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
                      >
                        <p className="font-medium text-gray-900">{team.company}</p>
                        <p className="text-sm text-gray-900">{team.displayName}</p>
                        {team.type === 'master' && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            {t('master')}
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-800 text-sm">{t('noTeamsAvailable')}</div>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Target Team Selection - Always visible */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('targetTeam')}</h2>
              <p className="text-sm text-gray-900 mb-4">{t('selectTeamToMoveTo')}</p>
              
              <div className="relative">
                <button
                  onClick={() => setIsTargetDropdownOpen(!isTargetDropdownOpen)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:bg-blue-50 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
                  disabled={!sourceTeam}
                >
                  {targetTeam ? (
                    <div>
                      <p className="font-medium text-gray-900">{targetTeam.company}</p>
                      <p className="text-sm text-gray-900">{targetTeam.displayName}</p>
                    </div>
                  ) : (
                    <span className="text-gray-800">
                      {sourceTeam ? t('selectTargetTeam') : t('selectSourceFirst')}
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${isTargetDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isTargetDropdownOpen && sourceTeam && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {availableTargetTeams.length > 0 ? (
                      availableTargetTeams.map((team) => (
                        <button
                          key={team.id}
                          onClick={() => handleTargetTeamSelect(team)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-100 hover:border-blue-300 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
                        >
                          <p className="font-medium text-gray-900">{team.company}</p>
                          <p className="text-sm text-gray-900">{team.displayName}</p>
                          {team.type === 'master' && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                              {t('master')}
                            </span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-800 text-sm">{t('noOtherTeams')}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - SmartProbe Selection */}
          {sourceTeam && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('selectSmartProbe')}</h2>
              <p className="text-sm text-gray-900 mb-4">{t('chooseSmartProbeToMove')}</p>

              {isFetchingProbes ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-900">{t('loadingSmartProbes')}</p>
                </div>
              ) : currentTeamProbes.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-gray-900">{t('noSmartProbesInTeam')}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {currentTeamProbes.map((probe) => {
                    // Determine display name and ID
                    const hasName = probe.trapName && probe.trapName !== 'None';
                    const displayName = hasName ? probe.trapName : `ID: ${probe.trapId}`;
                    const showId = hasName;
                    
                    return (
                      <button
                        key={probe.trapId}
                        onClick={() => handleProbeSelect(probe)}
                        className={`w-full p-4 border-2 rounded-lg text-left transition-all duration-200 cursor-pointer ${
                          selectedProbe?.trapId === probe.trapId
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {/* SmartProbe Name or ID */}
                            <div>
                              <span className="font-semibold text-gray-900">{displayName}</span>
                              {showId && (
                                <span className="ml-2 text-sm text-gray-800">ID: {probe.trapId}</span>
                              )}
                            </div>
                            
                            {/* Location and Building */}
                            <div className="mt-1 text-sm text-gray-900">
                              <p>{t('location')}: {probe.location || t('noLocationSet')}</p>
                              <p>{t('building')}: {probe.building || t('noBuildingSet')}</p>
                            </div>
                          </div>
                          {selectedProbe?.trapId === probe.trapId && (
                            <div className="ml-4">
                              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Move Button - Full width at bottom */}
        {sourceTeam && targetTeam && selectedProbe && (
          <div className="mt-6">
            <button
              onClick={handleMoveClick}
              disabled={isMoving}
              className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center font-semibold text-lg shadow-md"
            >
              {isMoving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('moving')}
                </>
              ) : (
                t('moveSmartProbeTitle')
              )}
            </button>
          </div>
        )}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && sourceTeam && targetTeam && selectedProbe && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowConfirmModal(false)} />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('confirmMove')}</h3>
              <p className="text-gray-900 mb-6">
                {t('confirmMoveMessage')} <span className="font-semibold">{selectedProbe.trapName || selectedProbe.trapId}</span> {t('from')}{' '}
                <span className="font-semibold">{sourceTeam.company}</span> {t('to')}{' '}
                <span className="font-semibold">{targetTeam.company}</span>?
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleMoveConfirm}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('confirmMoveButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
